import {
  FIXABLE_SLOT_CODES,
  ISlotStructureFinding,
  slotStructureValidatorFn,
} from "../structure/slotStructureValidator";

import { IOpenVesselDefinitionV1 } from "open-vessel-definition";
import { STAF_INTERMEDIATE_SLOT_FIELDS } from "../structure/consts";
import { cloneObject } from "@tedivo/tedivo-pure-helpers";
import { isPlainObject } from "../structure/patterns";

export interface IFixSlotStructureResult {
  /** The vessel with the fixable findings resolved. The input object when nothing changed */
  json: IOpenVesselDefinitionV1;
  /** What was fixed: `pos` set to the key, missing `sizes` created, empty slots made `restricted`, STAF leftovers removed */
  fixed: ISlotStructureFinding[];
  /** What `slotStructureValidator` reports and this helper does not touch */
  notFixed: ISlotStructureFinding[];
  changed: boolean;
}

/**
 * Resolves the lossless findings of `slotStructureValidator`: `pos` becomes the slot's
 * key; a slot without `sizes` gets `sizes: {}`; a slot that accepts no size and is not
 * `restricted` becomes `restricted: 1`, as the STAF import and `fixBay40sLocation` do;
 * fields left over from a STAF conversion are removed. The input is not modified.
 *
 * A `restricted` slot that also accepts sizes is left alone: dropping the flag would
 * make slots appear (`getSizesFromSlots` ignores restricted slots), dropping the sizes
 * would lose data.
 */
export function fixSlotStructure(json: IOpenVesselDefinitionV1): IFixSlotStructureResult {
  const { findings } = slotStructureValidatorFn({
    data: { json, lcgVcgTcgAndPairings: undefined, lcgsBy20Bay: undefined },
  }).result;

  const fixed = findings.filter((f) => FIXABLE_SLOT_CODES.includes(f.code));
  const notFixed = findings.filter((f) => !FIXABLE_SLOT_CODES.includes(f.code));

  if (fixed.length === 0) return { json, fixed, notFixed, changed: false };

  const newJson = cloneObject(json);

  (newJson.baysData as unknown[]).forEach((entry) => {
    if (!isPlainObject(entry) || !isPlainObject(entry["perSlotInfo"])) return;
    const perSlotInfo = entry["perSlotInfo"];

    for (const pos in perSlotInfo) {
      const slot = perSlotInfo[pos];
      if (!isPlainObject(slot)) continue;

      if (slot["pos"] !== pos) slot["pos"] = pos;
      if (!isPlainObject(slot["sizes"])) slot["sizes"] = {};

      const accepted = Object.values(slot["sizes"] as Record<string, unknown>).some(
        (v) => v === 1 || isPlainObject(v),
      );
      if (!accepted && slot["restricted"] !== 1) slot["restricted"] = 1;

      STAF_INTERMEDIATE_SLOT_FIELDS.forEach((f) => delete slot[f]);
    }
  });

  return { json: newJson, fixed, notFixed, changed: true };
}
